import {NgFor, NgIf} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef} from '@angular/material/bottom-sheet';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatSnackBar} from '@angular/material/snack-bar';
import {NahausRemindersService} from 'app/modules/admin/identity/services/nahausReminders.service';
import {deadlinePeriodKey, VAT_DEADLINE_LEAD_DAYS, VatDeadlineLeadDays, VatDeadlineRecipient} from '../../services/vat-deadline-tasks.logic';
import {VatDeadlinePeriod, VatDeadlineTasksResult, VatDeadlineTasksService} from '../../services/vat-deadline-tasks.service';
import {VAT_DEADLINE_COPY} from '../../vat-copy';

export type VatDeadlineSheetPeriod = VatDeadlinePeriod & { label: string; dueLabel: string; hasTask: boolean; isOverdue: boolean };

export interface VatDeadlineTasksSheetData {
    landlordID: string;
    landlordName: string;
    customerID: string | null;
    currentUserID: string;
    periods: VatDeadlineSheetPeriod[];
    users: VatDeadlineRecipient[];
}

/**
 * Bottom-Sheet „Aufgaben zu Abgabefristen“ (Spec 9): Zeiträume, Vorlauf 7/14 Tage, Erinnerungs-Empfänger (A8).
 * Vorauswahl: der nächste offene, nicht überfällige Zeitraum ohne Aufgabe.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Component({
    selector: 'vat-deadline-tasks-sheet',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgFor, NgIf, FormsModule, MatButtonModule, MatCheckboxModule, MatRadioModule, MatSelectModule, MatFormFieldModule, MatIconModule, MatProgressBarModule],
    providers: [VatDeadlineTasksService, NahausRemindersService],
    template: `
        <div class="p-6 pb-8 flex flex-col gap-5 max-w-full bg-card dark:bg-gray-900">
            <div>
                <h3 class="text-xl font-bold">{{ copy.title }}</h3>
                <p class="text-secondary text-md mt-1">{{ copy.subtitle }}</p>
            </div>

            <div class="flex flex-col gap-2">
                <div class="text-sm font-semibold">{{ copy.periods }}</div>
                <div *ngFor="let period of data.periods; trackBy: trackByPeriod"
                     class="flex items-center justify-between gap-3 rounded-xl border p-3 dark:border-gray-700"
                     [class.opacity-60]="period.hasTask">
                    <mat-checkbox color="primary" [checked]="isSelected(period)" [disabled]="period.hasTask || saving" (change)="toggle(period)">
                        <span class="font-medium">{{ period.label }}</span>
                        <span class="text-secondary text-sm ml-2">{{ copy.due(period.dueLabel) }}</span>
                    </mat-checkbox>
                    <span *ngIf="period.hasTask" class="rounded-full px-2.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300">{{ copy.alreadyCreated }}</span>
                    <span *ngIf="!period.hasTask && period.isOverdue" class="rounded-full px-2.5 py-0.5 text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300">{{ copy.overdue }}</span>
                </div>
            </div>

            <div class="flex flex-col gap-2">
                <div class="text-sm font-semibold">{{ copy.leadDays }}</div>
                <mat-radio-group color="primary" class="flex gap-6" [(ngModel)]="leadDays" [disabled]="saving">
                    <mat-radio-button *ngFor="let days of leadOptions" [value]="days">{{ copy.leadOption(days) }}</mat-radio-button>
                </mat-radio-group>
            </div>

            <mat-form-field class="w-full" subscriptSizing="dynamic">
                <mat-label>{{ copy.recipients }}</mat-label>
                <mat-select multiple [(ngModel)]="recipientIDs" [disabled]="saving">
                    <mat-option *ngFor="let user of data.users" [value]="user.id">{{ user.displayName }}</mat-option>
                </mat-select>
            </mat-form-field>

            <mat-progress-bar *ngIf="saving" mode="indeterminate"></mat-progress-bar>

            <div class="flex items-center justify-end gap-3">
                <button mat-stroked-button type="button" [disabled]="saving" (click)="sheetRef.dismiss()">{{ copy.cancel }}</button>
                <button mat-flat-button color="primary" type="button" data-testid="vat-deadline-confirm" [disabled]="!canConfirm()" (click)="confirm()">
                    <mat-icon class="icon-size-5" [svgIcon]="'heroicons_outline:bell'"></mat-icon>
                    <span class="ml-2">{{ saving ? copy.saving : copy.confirm }}</span>
                </button>
            </div>
        </div>
    `
})
export class VatDeadlineTasksSheetComponent {

    readonly copy = VAT_DEADLINE_COPY;
    readonly leadOptions = VAT_DEADLINE_LEAD_DAYS;
    selectedKeys = new Set<string>();
    leadDays: VatDeadlineLeadDays = 14;
    recipientIDs: string[];
    saving = false;

    constructor(
        public sheetRef: MatBottomSheetRef<VatDeadlineTasksSheetComponent, VatDeadlineTasksResult | undefined>,
        @Inject(MAT_BOTTOM_SHEET_DATA) public data: VatDeadlineTasksSheetData,
        private deadlineTasksService: VatDeadlineTasksService,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef,
    ) {
        this.recipientIDs = [data.currentUserID];
        const next = [...data.periods]
            .filter(p => !p.hasTask && !p.isOverdue)
            .sort((a, b) => a.dueDateEffective.getTime() - b.dueDateEffective.getTime())[0];
        if (next) {
            this.selectedKeys.add(deadlinePeriodKey(next.year, next.periodKey));
        }
    }

    trackByPeriod(_: number, period: VatDeadlineSheetPeriod): string {
        return deadlinePeriodKey(period.year, period.periodKey);
    }

    isSelected(period: VatDeadlineSheetPeriod): boolean {
        return this.selectedKeys.has(deadlinePeriodKey(period.year, period.periodKey));
    }

    toggle(period: VatDeadlineSheetPeriod): void {
        const key = deadlinePeriodKey(period.year, period.periodKey);
        if (this.selectedKeys.has(key)) {
            this.selectedKeys.delete(key);
        } else {
            this.selectedKeys.add(key);
        }
        this.cdr.markForCheck();
    }

    canConfirm(): boolean {
        return !this.saving && this.selectedKeys.size > 0 && !!this.data.currentUserID;
    }

    async confirm(): Promise<void> {
        if (!this.canConfirm()) {
            return;
        }
        this.saving = true;
        this.cdr.markForCheck();
        try {
            const result = await this.deadlineTasksService.create({
                landlordID: this.data.landlordID,
                landlordName: this.data.landlordName,
                customerID: this.data.customerID,
                uid: this.data.currentUserID,
                periods: this.data.periods.filter(p => this.isSelected(p)),
                leadDays: this.leadDays,
                recipients: this.data.users.filter(u => this.recipientIDs.includes(u.id)),
                now: new Date(),
            });
            this.snackBar.open(this.copy.result(result.created.length, result.skipped.length), 'OK', {duration: 5000});
            this.sheetRef.dismiss(result);
        } catch (error) {
            console.error('Fristen-Aufgaben konnten nicht angelegt werden -->', error);
            this.snackBar.open(this.copy.error, 'OK', {duration: 7000});
            this.saving = false;
            this.cdr.markForCheck();
        }
    }
}
